/**
 * @beemi/stream-parser
 * Main entry point for parsing and formatting stream events
 */

import { parseTikTokEvent } from './parsers/tiktok.js';
import { parseYouTubeEvent } from './parsers/youtube.js';
import { parseTwitchEvent } from './parsers/twitch.js';
import { formatForSDK } from './formatters/sdk-format.js';
import { MetricsTracker } from './metrics/MetricsTracker.js';

/**
 * Parse a raw stream event from any platform and format for SDK
 * @param {object} rawEvent - Raw event from platform
 * @param {string} platform - Platform name ('tiktok', 'youtube', 'twitch')
 * @param {string} sdkVersion - SDK version (e.g., '2.1.3', '2.1.5') - defaults to '2.1.5'
 * @returns {object|null} SDK-formatted event or null if parsing fails
 */
function parseAndFormat(rawEvent, platform, sdkVersion = '2.1.5') {
  const normalized = parseStreamEvent(rawEvent, platform);
  if (!normalized) return null;
  
  try {
    return formatForSDK(normalized, sdkVersion);
  } catch (error) {
    console.warn('[stream-parser] Format error:', error.message);
    return null;
  }
}

/**
 * Parse a raw stream event into normalized format
 * @param {object} rawEvent - Raw event from platform
 * @param {string} platform - Platform name
 * @returns {object|null} Normalized event or null
 */
function parseStreamEvent(rawEvent, platform) {
  if (!rawEvent || !platform) return null;
  
  const platformLower = platform.toLowerCase();
  
  try {
    switch (platformLower) {
      case 'tiktok':
        return parseTikTokEvent(rawEvent);
      case 'youtube':
        return parseYouTubeEvent(rawEvent);
      case 'twitch':
        return parseTwitchEvent(rawEvent);
      default:
        console.warn('[stream-parser] Unknown platform:', platform);
        return null;
    }
  } catch (error) {
    console.warn(`[stream-parser] Parse error (${platform}):`, error.message);
    return null;
  }
}

/**
 * Parse multiple events in batch
 * @param {Array} rawEvents - Array of raw events
 * @param {string} platform - Platform name
 * @param {string} sdkVersion - SDK version (e.g., '2.1.3', '2.1.5') - defaults to '2.1.5'
 * @returns {Array} Array of SDK-formatted events
 */
function parseStreamEvents(rawEvents, platform, sdkVersion = '2.1.5') {
  if (!Array.isArray(rawEvents)) return [];
  
  return rawEvents
    .map(event => parseAndFormat(event, platform, sdkVersion))
    .filter(event => event !== null);
}

export {
  parseAndFormat,
  parseStreamEvent,
  parseStreamEvents,
  MetricsTracker
};
